/**
 * Single-tenant underwriting reads for the BOV deck.
 *
 * The two tenancies hand their data to the print component differently, and this module
 * exists to keep that difference out of PrintBOV:
 *
 *   multi tenant   `tabs`   - nested tab -> table -> row, and the row builders take
 *                             (tabs, precomputed) with precompute coming from
 *                             BaseTabComponent
 *   single tenant  `tables` - a flat table -> row map, and the row builders take
 *                             (tables) only
 *
 * The builders in ../PrintSingleTenant/tables.js are reused as-is so the underwriting
 * extraction stays in one place and the two single-tenant decks cannot drift apart.
 */

import {
    getOfferingSummaryData,
    getLeaseSummaryData,
    getAnnualizedOperatingData,
    getNetOperatingIncomeData,
} from "../PrintSingleTenant/tables";
import {addCommasToNumber} from "../../utils";
import {pricingMatrixIndexOfReference} from "../UnderwritingSingleTenant/single-tenant-tables/constants";

/**
 * The offering summary is built from an explicit row list rather than by dropping rows from the
 * builder's own order, because the references ask for a different order as well as a different
 * subset. Every single-tenant deck prints the same six rows - the builder's Lot Size, Clear
 * Height, Doors and Office to Warehouse are all left out - and they differ only in where PSF
 * sits:
 *
 *   industrial          Price, PSF, Cap Rate, NOI, Year Built, Gross Leasable Area
 *   office / retail     Price, Cap Rate, PSF, NOI, Year Built, Gross Leasable Area
 *
 * That split is in the supplied artwork, not a reading of it: "Industrial - Single Tenant -
 * Proposal Template" puts PSF second and "Single Tenant Proposal Template" puts it third. If the
 * designer meant them to agree, this is the one place to change.
 */
const SUMMARY_ROWS_INDUSTRIAL = [
    'price',
    'price psf',
    'cap rate',
    'net operating income',
    'year built',
    'gross leasable area',
];

const SUMMARY_ROWS_DEFAULT = [
    'price',
    'cap rate',
    'price psf',
    'net operating income',
    'year built',
    'gross leasable area',
];

/**
 * The builder labels the row "Price PSF" and repeats the unit in the value ("$150 PSF"). The
 * reference heads the row "PSF", which would leave the unit printed twice, so the value is
 * reduced to the figure alone.
 */
const PSF_LABEL = 'PSF';

/**
 * The PSF printed on page 02 comes from the PRICING RANGE matrix, not from the Offering
 * Summary's own "Price PSF" field. The business asked for that on 2026-09-13: the Offering
 * Summary field is typed by hand and drifts, reading $100 on the deal that prompted it, where
 * the matrix computes its PSF from a price and so cannot go stale.
 *
 * The column is `pricingMatrixIndexOfReference` - the one the matrix anchors to the Offering
 * Summary's own cap rate, and so the one whose price is the price printed beside this figure.
 * That keeps the card internally consistent: on the deal that settled it, page 02 prints
 * $2,000,000 at 5.75% over 3,552 SF, and this reads $563, which is that price divided by that
 * area.
 *
 * It briefly read the leftmost column instead, a further bps step down and the most aggressive
 * the matrix carries. That printed $590 beside $2,000,000 - a figure belonging to a price the
 * deck does not show anywhere - and the business reversed it on 2026-09-17. If it is ever
 * moved off the reference column again, the price and cap rate on this card have to move with
 * it or the three stop agreeing.
 */
function pricingMatrixPsf(tables) {
    const row = (tables['pricing-matrix'] || {rows: {}}).rows['sale-price-psf'];
    const cell = row && row.valueCells[pricingMatrixIndexOfReference];
    if (!cell) return '';
    return cell.getComputedValue(tables).displayValue || '';
}

function key(label) {
    return String(label == null ? '' : label).trim().toLowerCase();
}

function summaryOrder(productType) {
    return key(productType) === 'industrial' ? SUMMARY_ROWS_INDUSTRIAL : SUMMARY_ROWS_DEFAULT;
}

/**
 * Orders the rows the reference asks for and drops the rest. Rows the underwriting cannot supply
 * are still printed - the card's height, and with it the page layout, has to stay fixed whatever
 * the data - so a missing row becomes an empty value and OfferingSummarySingle prints it as a
 * dash.
 */
function summaryRows(rows, productType, tables) {
    const byKey = {};
    rows.forEach(row => {
        byKey[key(row.label)] = row;
    });

    return summaryOrder(productType).map(wanted => {
        const row = byKey[wanted] || {label: wanted, value: ''};
        if (wanted !== 'price psf') return row;
        // The builder's own value is ignored - see pricingMatrixPsf. It is still ordered by
        // this key because that is what the reference calls the row.
        return {label: PSF_LABEL, value: pricingMatrixPsf(tables)};
    });
}

export function readProperty(tables) {
    const general = tables['general-information'].rows;
    return {
        name: general['property-name'].value,
        address: general['address'].value,
        city: general['city'].value,
        state: general['state'].value,
        zip: general['zip'].value,
    };
}

/**
 * Cell order in the pricing matrix is aggressive, anticipated, conservative from index 1,
 * the same as the multi-tenant sensitivity matrix and as the existing single-tenant deck
 * reads it (see PrintSingleTenant.render).
 */
export function readPricing(tables) {
    const matrix = tables['pricing-matrix'];
    const read = (rowKey, cellIndex, field) =>
        matrix.rows[rowKey].valueCells[cellIndex].getComputedValue(tables)[field] || '0';

    // PSF is only printed on the office and industrial decks - see BellCurve - but it is read
    // either way, so the decision about what prints stays on the page rather than here. The
    // multi-tenant read does the same; both matrices carry the row under the same key.
    const band = cellIndex => ({
        price: `$${addCommasToNumber(read('value', cellIndex, 'internalValue'))}`,
        psf: read('sale-price-psf', cellIndex, 'displayValue'),
        capRate: read('cap-rate', cellIndex, 'displayValue'),
    });

    return {
        aggressive: band(1),
        anticipated: band(2),
        conservative: band(3),
    };
}

/**
 * The facts band on the Value Analysis page. Unlike the multi-tenant deck this one prints
 * LEASE TYPE: a single-tenant property has one lease, so there is a single value to show.
 */
export function readFacts(tables) {
    const offering = tables['offering-summary'].rows;
    const noi = offering['net-operating-income'].getComputedValue(tables).displayValue;

    return [
        {value: offering['year-built'].value || '-', label: 'YEAR BUILT'},
        {value: noi || '-', label: 'NET OPERATING INCOME'},
        {value: tables['lease-summary'].rows['lease-type'].value || '-', label: 'LEASE TYPE'},
        {value: `${offering['lot-size'].value || '0'} Acres`, label: 'LOT SIZE'},
    ];
}

/**
 * Page 02's three blocks. The offering summary and lease summary builders return
 * {label, value} rows ready to print; the annualized operating data builder returns
 * rows of value cells in template column order.
 */
export function readPageTwo(tables, productType) {
    const summary = getOfferingSummaryData(tables);

    return {
        offeringSummary: {
            name: 'OFFERING SUMMARY',
            rows: summaryRows(summary, productType, tables),
        },
        // Every row the template prints, kept whatever the data - dropping the empty ones on
        // a vacant property changed the card's height and with it the page layout, which has
        // to stay fixed. Empty values print as a dash; see OfferingSummarySingle.
        leaseSummary: {
            name: 'LEASE SUMMARY',
            rows: getLeaseSummaryData(tables),
        },
        annualizedOperatingData: {
            name: 'ANNUALIZED OPERATING DATA',
            header: ['YEAR(S)', 'COMMENCEMENT', 'ANNUAL', 'INCREASE'],
            rows: getAnnualizedOperatingData(tables),
            netOperatingIncome: getNetOperatingIncomeData(tables),
            // The commencement column is only filled in when the deal is not a sale
            // leaseback - see the hidden flag getAnnualizedOperatingData sets on that
            // cell. It is dropped rather than printed empty.
            showCommencement: !isSaleLeaseBack(tables),
        },
    };
}

/**
 * Property photos are stored as either a full URL or a media-relative path, the same as
 * the existing single-tenant deck resolves them (see PrintSingleTenant.render).
 */
export function resolvePropertyImageUrl(propertyImage) {
    if (!propertyImage) return '';
    return String(propertyImage).includes('https')
        ? propertyImage
        : `${arc.path.media}${propertyImage}`;
}

function isSaleLeaseBack(tables) {
    return tables['general-information'].rows['sale-lease-back'].value === 'Yes';
}

/*
 * The schedule table carries four spare footerRow entries below the net operating income
 * line, and the template fills one of them with an option period
 * ("Option 1: 11-15  00/00/0000"). They are deliberately not printed here: in practice
 * agents use them for landlord expense headings - Taxes, Insurance, CAM, Management Fee -
 * typed without amounts, which read as unfinished on a client-facing page.
 *
 * Nothing is lost by leaving them out, because option periods also reach the deck as
 * ordinary schedule rows with the period in the YEAR(S) column, which is how they are
 * entered in practice. Restore this if a property is found that relies on the footer.
 */
